import { useCallback, useState } from 'react';
import { getFormSchema, getUiSchema } from 'api/file-management/formSchema';
import useFileManagement from 'api/file-management/useFileManagement';
import FormButtons from 'components/form/FormButtons';
import CustomForm from 'components/form/CustomForm';
import { FileManagement, FileManagementRequest } from 'api/model';
import UploadIcon from '@mui/icons-material/Upload';
import { useParams } from 'react-router';
import LoadingDialog from 'components/dialog/LoadingDialog';
import { Progress } from 'api/file-management/requests';
import { SettingsFormShell } from 'components/settingsHub/SettingsFormShell';

type Props = {
  onClose: () => void;
  onSubmit?: (formData: FileManagementRequest) => Promise<FileManagement>;
  formId?: string;
  showGlobalOption?: boolean;
  accept?: string;
  inShell?: boolean;
};

const UploadFileForm = ({
  onSubmit,
  onClose,
  formId = 'file-management-upload-form',
  showGlobalOption = false,
  accept,
  inShell = false,
}: Props) => {
  const [uploadProgress, setUploadProgress] = useState(0);

  const progressCallback = (progress: Progress) => setUploadProgress(progress.progress);
  const { projectId } = useParams();
  const { upload } = useFileManagement(projectId, undefined, progressCallback);
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = useCallback(
    (formData: FileManagementRequest) => {
      setIsLoading(true);
      void upload.mutateAsync(formData).finally(() => {
        setIsLoading(false);
        onClose();
        setUploadProgress(0);
      });
    },
    [upload, onClose],
  );

  const handleCustomSubmit = useCallback(
    (formData: FileManagementRequest) => {
      setIsLoading(true);
      setUploadProgress(0);
      void onSubmit!(formData).finally(() => {
        setUploadProgress(100);
        setIsLoading(false);
        onClose();
        setUploadProgress(0);
      });
    },
    [onSubmit, onClose],
  );

  const form = (
    <CustomForm
      schema={getFormSchema(showGlobalOption)}
      uiSchema={getUiSchema(showGlobalOption, accept)}
      onSubmit={onSubmit ? handleCustomSubmit : handleSubmit}
      isLoading={isLoading}
      formId={formId}
    />
  );

  const footer = (
    <FormButtons
      onCancel={onClose}
      submitBtnTxt="upload"
      submitBtnIcon={<UploadIcon />}
      isLoading={isLoading}
      formId={formId}
    />
  );

  const loadingDialog = <LoadingDialog isVisible={isLoading} progress={uploadProgress} title={'Uploading file...'} />;

  if (inShell) {
    return (
      <>
        <SettingsFormShell title="Upload File" footer={footer}>
          {form}
        </SettingsFormShell>
        {loadingDialog}
      </>
    );
  }

  return (
    <>
      {form}
      <div className="mt-6 flex justify-end">{footer}</div>
      {loadingDialog}
    </>
  );
};

export default UploadFileForm;
